import React, { useEffect, useRef, useState } from 'react'
import ListItem from '@mui/material/ListItem'
import Tooltip, {
  tooltipClasses,
  type TooltipProps,
} from '@mui/material/Tooltip'
import { styled } from '@mui/material/styles'
import { SegmentShape } from './SegmentShape'
import { ProgressSegmentProps } from './typings'

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: '#a0a0a0',
    boxShadow: theme.shadows[1],
    fontSize: 12,
    padding: 8,
  },
})) as React.ComponentType<TooltipProps>

/**
 * Individual progress segment with responsive behavior.
 *
 * Mobile adaptations:
 * - Reduced font size (11px vs 12px)
 * - Tighter padding
 * - Min 44px touch target via padding/height
 * - Uses CSS :hover via MUI `sx` (no-op on touch devices naturally)
 */
export const ProgressSegment: React.FC<ProgressSegmentProps> = React.memo(
  (props) => {
    const {
      color,
      colorHover,
      disabled,
      endAdornment,
      height = 33,
      isMobile,
      isOpen,
      marginLeft,
      onClick,
      padding,
      text,
      textColor,
      textColorHover,
      transitionTime,
      variant,
      width,
      dataTestId,
    } = props

    const textRef = useRef<HTMLDivElement>(null)
    const [isTextTruncated, setIsTextTruncated] = useState(false)

    // Detect text overflow for tooltip
    useEffect(() => {
      if (textRef.current) {
        setIsTextTruncated(
          textRef.current.scrollWidth > textRef.current.offsetWidth,
        )
      }
    }, [text, width])

    const fontSize = isMobile ? 11 : 12
    const labelPadding = isMobile ? adjustPaddingForMobile(padding) : padding

    return (
      <StyledTooltip title={isTextTruncated && text ? text : ''}>
        <ListItem
          // 44px min touch target on mobile; :hover is a no-op on pure touch devices, :active gives touch feedback
          sx={{
            marginLeft: `${marginLeft}px`,
            padding: 0,
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            cursor: disabled ? 'default' : 'pointer',
            userSelect: 'none',
            minHeight: isMobile ? 44 : undefined,
            '& .segment-label': {
              color: textColor,
              transition: `color ${transitionTime ?? 0.5}s`,
            },
            '&:hover .segment-label': disabled ? {} : { color: textColorHover },
            '& .segment-shape svg path, & .segment-shape svg polygon': {
              fill: color,
              transition: `fill ${transitionTime ?? 0.5}s`,
            },
            '&:hover .segment-shape svg path, &:hover .segment-shape svg polygon':
              disabled ? {} : { fill: colorHover },
            '&:active .segment-shape svg path, &:active .segment-shape svg polygon':
              disabled ? {} : { fill: colorHover },
            '&:active .segment-label': disabled
              ? {}
              : { color: textColorHover },
          }}
          onClick={onClick}
          data-test-id={dataTestId}
        >
          {/* SVG Background Shape */}
          <span className="segment-shape" style={{ display: 'flex' }}>
            <SegmentShape
              color={color}
              height={height}
              width={width ?? 100}
              variant={variant}
              transitionTime={transitionTime}
            />
          </span>

          {/* Text Label */}
          {text && text.length > 0 && (
            <div
              ref={textRef}
              className="segment-label"
              style={{
                fontFamily: 'Source Sans Pro Semibold',
                fontSize,
                lineHeight: `${height}px`,
                width: '100%',
                height: `${height}px`,
                padding: labelPadding,
                position: 'absolute',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {text}
            </div>
          )}

          {/* End Adornment (e.g. chevron dropdown) */}
          {endAdornment && (
            <div
              style={{
                display: 'inline-block',
                position: 'absolute',
                right: isMobile ? 8 : 12,
              }}
            >
              {endAdornment(isOpen)}
            </div>
          )}
        </ListItem>
      </StyledTooltip>
    )
  },
)

ProgressSegment.displayName = 'ProgressSegment'

// ==================== Helpers ====================

/**
 * Reduce horizontal padding for mobile to give more room to text.
 * e.g. '0 34px' → '0 20px', '0 12px' → '0 8px'
 */
function adjustPaddingForMobile(padding: string): string {
  return padding.replace(/(\d+)px/g, (_match, value) => {
    const num = parseInt(value, 10)
    if (num === 0) return '0px'
    return `${Math.max(4, Math.round(num * 0.6))}px`
  })
}
